import React, { Suspense } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { useAppSelector } from "./hooks/reduxHooks";
import { selectIsLoggedIn } from "./store/authStore";
import { getEnvironmentType } from "./utils/utils";
import { ENV_TYPES } from "./types/env.types";

const HomePage = React.lazy(() => import("./pages/home"));
const CheckoutPage = React.lazy(() => import("./pages/CheckoutPage"));
const SignupPage = React.lazy(() => import("./pages/Signup"));
const LoginPage = React.lazy(() => import("./pages/Login"));
const RegisterPage = React.lazy(() => import("./pages/SignUpEmail"));
const ForgotPasswordPage = React.lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
// const VerifyEmailPage = React.lazy(() => import("./pages/EmailVerification"));
const VerifyPhonePage = React.lazy(() => import("./pages/PhoneVerification"));
const SuccessPage = React.lazy(() => import("./pages/SuccessPage"));
const VippsAuth = React.lazy(() => import("./pages/onboard"));
const VerifyBankId = React.lazy(() => import("./pages/verifyBankId"));
const Components = React.lazy(() => import("./pages/components"));
const PaystackSuccessPage = React.lazy(() => import("./pages/PaystackSuccessPage"));
const SumsubVerification = React.lazy(() => import("./pages/SumsubVerification"));
const SetNewPassword = React.lazy(() => import("./pages/SetNewPassword"));


// local react
const ROUTES_LOCAL = {
  HOME: "/",
  RESERVE_STORAGE: "/reserve-storage",
  CONFIRM_RESERVATION: "/confirm-reservation",
  SIGNUP: "/signup",
  LOGIN: "/login",
  SIGNUP_EMAIL: "/signup-email",
  VERIFY_EMAIL: "/verify-email",
  VERIFY_PHONE: "/verify-phone",
  FORGOT_PASSWORD: "/forgot-password",
  RESET_PASSWORD: "/reset-password",
  COMPONENTS: "/components",
  CHECKOUT_STEP_1: "/checkout-step-1",
  CHECKOUT_STEP_2: "/checkout-step-2",
  MANUAL_ID_CHECK: "/manual-check",
  VIPPS_AUTH: "/onboard",
  BANK_ID_AUTH: "/verify-id",
  PAYMENT_SUCCESS: "/success",
  PAYSTACK_SUCCESS: "/payment-success",
  SUMSUB_VERIFICATION: "/sumsub-verification",
  SET_NEW_PASSWORD: "/set-new-password"
};

// live / local wp
const ROUTES_WP = {
  HOME: "/",
  RESERVE_STORAGE: "/reserve-storage",
  CONFIRM_RESERVATION: "/reserve-storage/confirm-reservation",
  SIGNUP: "/reserve-storage/signup",
  LOGIN: "/reserve-storage/login",
  SIGNUP_EMAIL: "/reserve-storage/signup-email",
  VERIFY_EMAIL: "/reserve-storage/verify-email",
  VERIFY_PHONE: "/reserve-storage/verify-phone",
  FORGOT_PASSWORD: "/reserve-storage/forgot-password",
  RESET_PASSWORD: "/reserve-storage/reset-password",
  COMPONENTS: "/reserve-storage/components",
  MANUAL_ID_CHECK: "/reserve-storage/manual-check",
  VIPPS_AUTH: "/reserve-storage/onboard",
  BANK_ID_AUTH: "/reserve-storage/verify-id",
  PAYMENT_SUCCESS: "/reserve-storage/success",
  PAYSTACK_SUCCESS: "/reserve-storage/payment-success",
  SUMSUB_VERIFICATION: "/reserve-storage/sumsub-verification",
  SET_NEW_PASSWORD: "/reserve-storage/set-new-password"
};


/**
 * Get the appropriate routes based on environment type
 * @returns {object} The routes object
 */
const getRoutes = () => {
  const envType = getEnvironmentType();
  const envMode = import.meta.env.MODE;

  // Use LOCAL routes for development environment or if 'local' is in the env mode
  if (envType === ENV_TYPES.DEVELOPMENT || (envMode && envMode.toLowerCase().includes('local'))) {
    return ROUTES_LOCAL;
  }
  
  if (envType === ENV_TYPES.PRODUCTION || envType === ENV_TYPES.STAGING) {
    return ROUTES_WP;
  }

  
  // This should never be reached due to the error throw in getEnvironmentType
  throw new Error('Could not determine routes for the current environment.');
};

export const ROUTES = getRoutes();

const Loading = () => <div>Loading</div>;

const ProtectedRoute = () => {
  const userIsLoggedIn = useAppSelector(selectIsLoggedIn);

  if (!userIsLoggedIn) {
    return <Navigate to={ROUTES.LOGIN} />;
  }

  return <Outlet />;
};

const Router: React.FC = () => {
  return (
    <Routes>
      <Route path={ROUTES.HOME} element={<HomePage />} />
      <Route path={ROUTES.RESERVE_STORAGE} element={<CheckoutPage />} />
      <Route path={ROUTES.CONFIRM_RESERVATION} element={<Navigate to={ROUTES.RESERVE_STORAGE} replace />} />
      <Route path={ROUTES.LOGIN} element={<LoginPage />} />
      <Route path={ROUTES.SIGNUP} element={<SignupPage />} />
      <Route path={ROUTES.SIGNUP_EMAIL} element={<RegisterPage />} />
      <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
      <Route path={ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
      <Route path={ROUTES.VIPPS_AUTH} element={<VippsAuth />} />
      <Route path={ROUTES.BANK_ID_AUTH} element={<VerifyBankId />} />
      {/*<Route path={ROUTES.VERIFY_EMAIL} element={<VerifyEmailPage />} />*/}
      <Route path={ROUTES.VERIFY_PHONE} element={<VerifyPhonePage />} />
      {/* <Route path={ROUTES.PAYMENT_SUCCESS} element={<SuccessPage />} /> */}
      <Route path={ROUTES.SUMSUB_VERIFICATION} element={<SumsubVerification />} />
      <Route path={ROUTES.PAYSTACK_SUCCESS} element={<PaystackSuccessPage />} />
      <Route path={ROUTES.SET_NEW_PASSWORD} element={<SetNewPassword />} />


      {/* Authentication Required in all of these below Routes */}
      <Route element={<ProtectedRoute />}>
        <Route
          path={ROUTES.COMPONENTS}
          element={
            <Suspense fallback={<Loading />}>
              <Components />
            </Suspense>
          }
        />
      </Route>

      <Route
        path={ROUTES.PAYMENT_SUCCESS}
        element={
          <Suspense fallback={<Loading />}>
            <SuccessPage />
          </Suspense>
        }
      />
    </Routes>
  );
};

export default Router;
